/**
 * Service copy, transcribed verbatim from the approved design.
 *
 * The Home summaries and the Services page detail are deliberately separate
 * arrays: the design wrote different, shorter copy for the Home cards. They are
 * not two renderings of one list.
 */

export interface ServiceCard {
  /** Two-digit ordinal shown in the card corner. */
  index: string;
  title: string;
  description: string;
}

/** Home - "What we do", six cards. */
export const SERVICES_SUMMARY: readonly ServiceCard[] = [
  {
    index: '01',
    title: 'eCommerce websites & portals',
    description:
      'Storefronts and B2B portals of different sizes and complexity, built to carry real trading volume.',
  },
  {
    index: '02',
    title: 'Custom eCommerce solutions',
    description: 'Pricing, catalogue and checkout logic beyond what a platform ships with.',
  },
  {
    index: '03',
    title: 'Platform apps & extensions',
    description:
      'Applications and extensions for Adobe Commerce, Shopify, Shopware and BigCommerce.',
  },
  {
    index: '04',
    title: 'Integrations & automation',
    description: 'ERP, PIM, logistics and payment systems, connected and kept in sync.',
  },
  {
    index: '05',
    title: 'Web apps, APIs & backends',
    description: 'Internal tools, customer portals and the services running behind them.',
  },
  {
    index: '06',
    title: 'Consulting & architecture',
    description: 'Stack reviews, migration planning and solution design.',
  },
];

export interface ServiceDetail {
  title: string;
  description: string;
  tags: readonly string[];
}

/** Services page - "eCommerce", six detailed cards. */
export const SERVICES_DETAIL: readonly ServiceDetail[] = [
  {
    title: 'eCommerce websites & portals',
    description:
      'From a focused single-market catalogue to a multi-market B2B portal with account-level pricing and approval flows. We build on established platforms where they fit and custom where they do not.',
    tags: ['Storefronts', 'B2B portals', 'Multi-market'],
  },
  {
    title: 'Custom eCommerce solutions',
    description:
      "Pricing rules, configurable products, complex tax and shipping logic, bespoke checkout steps - the parts of a shop that follow the business rather than the platform's defaults.",
    tags: ['Pricing logic', 'Checkout', 'Catalogue'],
  },
  {
    title: 'Platform applications & extensions',
    description:
      "Public and private applications, admin extensions and storefront customisation for Adobe Commerce, Shopify, Shopware and BigCommerce - built to each platform's own requirements and review standards.",
    tags: ['Public apps', 'Private apps', 'Extensions'],
  },
  {
    title: 'Support, maintenance & performance',
    description:
      'Ongoing care for a shop that is already trading: platform and security updates, fixes, performance work and a known team to call when something breaks.',
    tags: ['Updates', 'Performance', 'Monitoring'],
  },
  {
    title: 'Platform & business system integrations',
    description:
      'Connecting shops to ERP, PIM, WMS, accounting, logistics and payment providers - with idempotent jobs, sensible retries and monitoring so failures surface early.',
    tags: ['ERP · PIM', 'Logistics', 'Payments'],
  },
  {
    title: 'Technical consulting & solution architecture',
    description:
      'A second opinion on an existing stack, a replatforming plan, or the architecture for something not built yet - delivered as a document you can act on, with or without us.',
    tags: ['Reviews', 'Migrations', 'Architecture'],
  },
];

/** Services page - "Beyond eCommerce", four columns. */
export const BEYOND_ECOMMERCE: readonly Omit<ServiceCard, 'index'>[] = [
  {
    title: 'Custom web applications',
    description:
      'Internal tools, customer portals and operational dashboards built around a specific workflow.',
  },
  {
    title: 'Backend systems, APIs & automation',
    description:
      'Services, scheduled jobs and APIs that remove manual steps and keep systems in agreement.',
  },
  {
    title: 'Roblox game development',
    description:
      'Games and interactive experiences on Roblox, from mechanics and systems design to live operation.',
  },
  {
    title: 'Other custom software',
    description:
      'If it is well specified and we are the right team for it, we will say so. If we are not, we will say that too.',
  },
];

/** Home - platforms band. */
export const PLATFORMS: readonly string[] = [
  'Adobe Commerce',
  'Shopify',
  'Shopware',
  'BigCommerce',
];

/** Home - "Beyond eCommerce" capability chips. */
export const BEYOND_TAGS: readonly string[] = [
  'Custom software',
  'Roblox & interactive',
  'Backends & APIs',
  'Data & reporting',
  'Automation',
];

/** Home - "How we work", four steps. */
export const PROCESS: readonly ServiceCard[] = [
  {
    index: 'STEP 01',
    title: 'Understand the business',
    description:
      'Before the stack: what the operation needs to do, and what is actually slowing it down.',
  },
  {
    index: 'STEP 02',
    title: 'Scope honestly',
    description:
      'A clear plan with known trade-offs - including the parts we would advise against building.',
  },
  {
    index: 'STEP 03',
    title: 'Build in visible increments',
    description: 'Working software you can review as it goes live, not status reports about it.',
  },
  {
    index: 'STEP 04',
    title: 'Hand over cleanly',
    description: 'Documented, maintainable and yours to keep - with support if you want it.',
  },
];

/** Home - "Why clients work with us", four points. */
export const DIFFERENTIATORS: readonly Omit<ServiceCard, 'index'>[] = [
  {
    title: 'Senior people, directly',
    description:
      'You talk to the engineers doing the work. Nothing is relayed through an account layer.',
  },
  {
    title: 'Commerce is the day job',
    description:
      'Catalogues, tax, stock, payments and the awkward edge cases are familiar territory.',
  },
  {
    title: 'Small enough to move',
    description: 'Short decision paths, quick answers, and a team you can reach the same day.',
  },
  {
    title: 'Built to be maintained',
    description: 'Readable code, documented decisions, no dependency on us to keep it running.',
  },
];

/** Company page - engagement models. */
export const ENGAGEMENT_MODELS: readonly { name: string; detail: string }[] = [
  { name: 'Project delivery', detail: 'Fixed scope, agreed milestones' },
  { name: 'Ongoing development', detail: 'Continuous work on a live product' },
  { name: 'Consulting', detail: 'Reviews, architecture, planning' },
  { name: 'Subcontracting', detail: 'Specialist capacity for agencies' },
];

/**
 * Company page - frequently asked questions.
 *
 * Serves three audiences at once, which is why it earns its place:
 *
 *   - **Search.** Emitted as `FAQPage` structured data, so the answers are
 *     eligible to appear directly in results.
 *   - **Answer engines.** Each answer is written to stand alone. An LLM
 *     summarising "who is Skabro" can lift one of these whole and have it still
 *     be true and attributable out of context - which the marketing copy
 *     elsewhere on the site, by design, cannot.
 *   - **Payment and procurement review.** Every question here is one a payment
 *     provider's onboarding review or a client's finance team asks anyway.
 *
 * Keep answers factual, self-contained, and free of pronouns that need the
 * previous sentence to resolve. Resist adding voice; the rest of the site
 * carries that.
 */
export const FAQ: readonly { question: string; answer: string }[] = [
  {
    question: 'What does Skabro do?',
    answer:
      'Skabro s. r. o. builds eCommerce systems and custom software for business clients: online ' +
      'stores and B2B portals, custom commerce logic, applications and extensions for eCommerce ' +
      'platforms, integrations with ERP, PIM and logistics systems, and technical consulting and ' +
      'solution architecture.',
  },
  {
    question: 'Where is Skabro based, and who does it work with?',
    answer:
      'Skabro s. r. o. is registered in Bratislava, Slovakia, and works remotely with clients ' +
      'across the European Union. Clients are typically growing merchants who need a technical ' +
      'partner, and agencies or larger organisations who need a specialist team alongside their ' +
      'own. Skabro contracts with businesses and organisations rather than with consumers.',
  },
  {
    question: 'Which eCommerce platforms does Skabro work with?',
    answer:
      'Adobe Commerce (Magento), Shopify, Shopware and BigCommerce, plus headless and fully ' +
      'custom builds where an established platform is not the right fit. Skabro builds public ' +
      'and private applications and extensions for each of those platforms, to their own review ' +
      'standards.',
  },
  {
    question: 'How does Skabro price work, and in what currency?',
    answer:
      'Work is quoted individually and invoiced in euro (EUR). Skabro does not publish list ' +
      'prices, because scope determines cost. Clients receive a written quotation stating the ' +
      'fee, the currency and the payment schedule before work begins, and nothing is chargeable ' +
      'until the quotation is accepted in writing.',
  },
  {
    question: 'Is Skabro VAT registered?',
    answer:
      'Yes. Skabro s. r. o. is a registered VAT payer in Slovakia under §4 of the Slovak VAT Act, ' +
      'with VAT identification number SK2122432895 and company registration number (IČO) ' +
      '56788207. Invoices to VAT-registered businesses in other EU member states are issued ' +
      'under the reverse charge mechanism.',
  },
  {
    question: 'How are contracts and invoicing handled?',
    answer:
      'Every engagement begins with a signed contract or written order confirmation. Fixed-scope ' +
      'projects are invoiced against agreed milestones; ongoing work is invoiced monthly in ' +
      'arrears. Invoices are payable within 14 days unless the agreement states otherwise. ' +
      'Skabro can supply the documentation that partners and payment providers usually ask for.',
  },
  {
    question: 'What happens after I send an enquiry?',
    answer:
      'A person reads it and replies, usually within one working day. The first response is an ' +
      'honest view of scope, approach and whether Skabro is the right fit - including saying so ' +
      'when it is not. There is no sales sequence and no automated follow-up.',
  },
];
